import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { AlertTriangle, RefreshCw, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CHECK_INTERVAL = 5 * 60 * 1000; // re-check every 5 min

// Global banner that warns when a CONNECTED store's Shopify token has gone stale
// (expiring offline tokens live ~1h and rotate). Offers a one-click reconnect that
// reuses the authenticated begin-connect flow so the store stays linked to this org.
const ShopifyConnectionBanner = () => {
  const [status, setStatus] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopify/connection-status`);
      setStatus(res.data);
    } catch {
      // Silent — never block the app on a health check.
    }
  }, []);

  useEffect(() => {
    checkStatus();
    const id = setInterval(checkStatus, CHECK_INTERVAL);
    return () => clearInterval(id);
  }, [checkStatus]);

  // Only surface when a store IS configured but its connection is broken/stale.
  // Never-connected accounts are guided from Settings, not nagged here.
  if (!status || status.connected || !status.shop || dismissed) return null;

  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      const resp = await axios.post(
        `${API}/shopify/begin-connect?shop=${encodeURIComponent(status.shop)}`
      );
      if (resp.data?.authorize_url) {
        window.location.href = resp.data.authorize_url;
        return;
      }
      throw new Error("No authorize URL returned");
    } catch {
      toast.error("Could not start Shopify reconnect. Open Settings → Shopify.");
      setReconnecting(false);
    }
  };

  return (
    <div
      data-testid="shopify-connection-banner"
      className="sticky top-0 z-40 bg-amber-50 border-b border-amber-300"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            Shopify connection needs attention
          </p>
          <p className="text-xs text-amber-700 truncate">
            <span className="font-mono">{status.shop}</span>
            {" — "}
            {status.needs_reauth
              ? "authorisation expired. Reconnect to keep syncing orders and fulfilments."
              : status.message || "the store is not connected right now."}
          </p>
        </div>
        <button
          onClick={handleReconnect}
          disabled={reconnecting}
          data-testid="banner-reconnect-shopify"
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
        >
          {reconnecting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {reconnecting ? "Redirecting…" : "Reconnect Shopify"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          data-testid="banner-dismiss"
          aria-label="Dismiss"
          className="flex-shrink-0 p-1.5 rounded-md text-amber-500 hover:text-amber-800 hover:bg-amber-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ShopifyConnectionBanner;
